import type { PermissionRule } from '@opencode/client/promise';

export const APPROVAL_MODES = ['supervised', 'auto-accept-edits', 'full-access'] as const;

export type ApprovalMode = (typeof APPROVAL_MODES)[number];

export const DEFAULT_APPROVAL_MODE: ApprovalMode = 'full-access';

const ask = (action: string, resource: string): PermissionRule => ({
  action,
  resource,
  effect: 'ask',
});

// A helper copies its parent's rules, and its request gets no card yet (#9392), so the modes that
// ask start no helpers.
const NO_HELPERS: PermissionRule = { action: 'subagent', resource: '*', effect: 'deny' };

/*
 * Adapted from T3 Code's OpenCode 2 adapter (packages/provider-opencode/src/server/v2/adapter.ts,
 * `sessionRules`), commit 0ae1091d019484d474fce0fe0a89195a82134041. MIT License.
 * Copyright (c) 2026 T3 Tools Inc. Full license: /third-party-licenses.txt
 */
/**
 * OpenCode checks a session's rules after the agent's rules and every config's, the folder's
 * `opencode.json` included, and the last rule that matches decides, so a mode only adds asks on
 * top of plain OpenCode and the folder's own settings. None is `* allow`, T3 Code's Full
 * access: it would silence the folder's asks and the `.env` asks, and lift the limits of the
 * helpers the agent starts, which copy their parent's rules. None asks for `external_directory`:
 * that would also ask before the agent reads its own saved output in OpenCode's data folder.
 * OpenCode names an edit's file by its path from the folder, `../…` elsewhere in the folder's
 * repository, and by its absolute path beyond that (`core/src/file-access.ts` in 2.0.26), and `*`
 * matches across `/`.
 */
const RULES: Record<ApprovalMode, readonly PermissionRule[]> = {
  'supervised': [ask('shell', '*'), ask('edit', '*'), NO_HELPERS],
  'auto-accept-edits': [ask('shell', '*'), ask('edit', '../*'), ask('edit', '/*'), NO_HELPERS],
  'full-access': [],
};

export function approvalRules(mode: ApprovalMode): readonly PermissionRule[] {
  return RULES[mode];
}

export function approvalModeOf(rules: readonly PermissionRule[] | undefined): ApprovalMode {
  const asks = (action: string) =>
    rules?.findLast((rule) => rule.action === action && rule.resource === '*')?.effect === 'ask';
  if (asks('edit')) {
    return 'supervised';
  }
  return asks('shell') ? 'auto-accept-edits' : 'full-access';
}
